import { Description, Disclosure, DisclosureButton, DisclosurePanel, Field, Label, Switch } from '@headlessui/react'
import { ChevronRightIcon, PlusIcon, XCircleIcon } from '@heroicons/react/20/solid'
import { Bars3Icon, FolderPlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline'
import _ from 'lodash'
import moment from 'moment'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useSpinDelay } from 'spin-delay'
import styled from 'styled-components'
import { z } from 'zod'

import Page from '@/components/Page'
import PaginationTable from '@/components/PaginationTable'
import Pill from '@/components/Pill'
import useFeatures from '@/components/useFeatures'
import User from '@/components/User'
import { useAppSelector } from '@/store/hooks'
import { getSpaces } from '@/store/selectors'
import cn from '@/utilities/cn'
import { zodResolver } from '@/utilities/zodResolver'

import Audience from './components/Audience'
import Modal from './components/Modal'
import Panel from './components/Panel'
import { Sortable, SortableItem } from './components/Sortable'
import messages from './messages'

const StyledPage = styled(Page)`
  &,
  .content,
  .page-wrapper {
    background-color: #fff;
  }
`

// PaginationTable does not allow passing in classNames for all components
const StyledTableWrapper = styled('div')`
  .loader__spinner {
    width: 1rem;
    height: 1rem;
  }

  .sort-container {
    cursor: pointer;
    align-items: center;
  }
  .sort-container .sort-value {
    margin-left: 0.25rem;
  }
  .sort-container .sort {
    position: initial;
  }

  .flat-table tbody tr:hover {
    background-color: initial;
  }

  .controls {
    align-items: center;
    padding-inline: 0.5rem;
  }
  .controls .page-display-count {
    padding: 10px 0;
  }
`

const WIDGETS = [
  {
    label: 'Toolbar (v5)',
    value: 'toolbar-v5',
    feature: 'console-organization-client-configs-widgets-toolbar-v5'
  },
  {
    label: 'Floating Action Button (FAB)',
    value: 'nueva-latest',
    feature: 'console-organization-client-configs-widgets-nueva-latest'
  },
  {
    label: 'Floating Action Button (FAB) - alpha',
    value: 'nueva-alpha',
    feature: 'console-organization-client-configs-widgets-nueva-alpha'
  },
  {
    label: 'Toolbar (2.0)',
    value: 'toolbar-latest',
    feature: 'console-organization-client-configs-widgets-toolbar-latest'
  },
  {
    label: 'Toolbar (2.0) - alpha',
    value: 'toolbar-alpha',
    feature: 'console-organization-client-configs-widgets-toolbar-alpha'
  },
  {
    label: 'Custom',
    value: 'custom',
    feature: 'console-organization-client-configs-widgets-custom'
  }
]

const coerce = <T extends z.ZodType>(ctor: (value: unknown) => unknown, schema: T) => {
  return z.preprocess(value => (value ? ctor(value) : undefined), schema)
}

const stringifyObject = (value: unknown) => {
  return typeof value === 'string' ? value : JSON.stringify(value, null, 2)
}

// https://zod.dev/?id=transform - Transform does not catch errors
const parseJsonTransform = (value: string, ctx: z.RefinementCtx) => {
  try {
    return JSON.parse(value)
  } catch (error) {
    ctx.addIssue({
      code: 'custom',
      message: String(error)
    })
    return z.NEVER
  }
}

const coerceEmptyStringToUndefined = (value: string, ctx: z.RefinementCtx) => {
  if (value === '') return

  ctx.addIssue({
    code: 'custom',
    message: 'Value must be an empty string'
  })
  return z.NEVER
}

const stringifiable = (schema: z.ZodType) => {
  return z
    .union([
      // The raw value
      schema,
      // The stringified value
      z.string().transform(parseJsonTransform),
      // Convert empty string to undefined (useful for optional values)
      z.string().transform(coerceEmptyStringToUndefined)
    ])
    .pipe(schema)
}

const clientConfigSchema = z.object({
  id: z.string(),
  key: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[\da-z-]+$/, 'String must contain only lower case letters, numbers, and hyphens'),
  config: z.object({
    schemaVersion: coerce(Number, z.number().default(1)),
    audience: coerce(String, z.string().optional()),
    space: coerce(String, z.string().optional()),
    supportUrl: coerce(String, z.url().optional()),
    label: coerce(String, z.string().optional()),
    icon: coerce(String, z.url().optional()),
    widget: coerce(String, z.enum(WIDGETS.map(w => w.value) as [(typeof WIDGETS)[number]['value']]).optional()),
    position: coerce(String, z.string().optional()),
    theme: coerce(String, z.string().optional()),
    prompt: coerce(String, z.string().optional()),
    features: stringifiable(z.record(z.string(), z.unknown()).optional()).optional(),
    rules: z
      .array(
        z.object({
          name: coerce(String, z.string()),
          audience: coerce(String, z.string().optional()),
          space: coerce(String, z.string().optional()),
          supportUrl: coerce(String, z.url().optional()),
          label: coerce(String, z.string().optional()),
          icon: coerce(String, z.url().optional()),
          widget: coerce(String, z.enum(WIDGETS.map(w => w.value) as [(typeof WIDGETS)[number]['value']]).optional()),
          position: coerce(String, z.string().optional()),
          theme: coerce(String, z.string().optional()),
          prompt: coerce(String, z.string().optional()),
          features: stringifiable(z.record(z.string(), z.unknown()).optional()).optional()
        })
      )
      .optional()
  }),
  isDefault: z.boolean().default(false),
  createdAt: z.string(),
  createdBy: z.string(),
  updatedAt: z.string(),
  updatedBy: z.string()
})

type ClientConfig = z.infer<typeof clientConfigSchema>

function OrganizationClientConfigsPage() {
  const intl = useIntl()

  const [action, setAction] = useState<
    { type: 'upsert'; data?: ClientConfig } | { type: 'delete'; data: ClientConfig }
  >()

  const clientConfigsQuery = useQuery<{ data: ClientConfig[] }>({
    queryKey: ['/v1/admin/organizations/:organizationId/client-configs']
  })

  function handleActionCancel() {
    setAction(undefined)
  }

  function handleUpsertClick(clientConfig?: ClientConfig) {
    setAction({ type: 'upsert', data: clientConfig })
  }

  function handleDeleteClick(clientConfig: ClientConfig) {
    setAction({ type: 'delete', data: clientConfig })
  }

  const isLoading = clientConfigsQuery.isLoading

  const clientConfigs = clientConfigsQuery.data?.data ?? []

  return (
    <div className="w-full">
      <StyledPage isLoading={useSpinDelay(isLoading, { delay: 500, minDuration: 200 })}>
        <Page.Header
          className="my-4 p-0"
          title={intl.formatMessage(messages.title)}
          buttons={
            clientConfigs.length > 0 ? (
              <button
                type="button"
                className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                onClick={() => handleUpsertClick()}
              >
                {/* <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" /> */}
                {intl.formatMessage(messages.addClientConfig)}
              </button>
            ) : null
          }
        >
          Need help? Please see our{' '}
          <a href="https://help.workgrid.com/s/article/Managing-Client-Configurations" className="[font-size:inherit]">
            Managing Client Configurations
          </a>{' '}
          help center article.
        </Page.Header>

        {clientConfigs.length > 0 ? (
          <StyledTableWrapper className="my-2">
            <PaginationTable
              className="overflow-hidden rounded bg-white outline outline-1 outline-gray-200"
              columns={[
                {
                  property: 'key',
                  props: {
                    className: 'w-full'
                  },
                  header: {
                    label: intl.formatMessage(messages.keyLabel),
                    props: { className: '!bg-gray-100 !font-bold !text-gray-600' },
                    sortable: true
                  },
                  cell: {
                    formatters: [
                      (key: string, { rowData }: { rowData: ClientConfig }) => (
                        <div className="break-all">
                          <span className="font-semibold">{key}</span>
                          {rowData.isDefault && (
                            <Pill className="-my-4 ml-4">{intl.formatMessage(messages.defaultLabel)}</Pill>
                          )}
                        </div>
                      )
                    ]
                  }
                },
                {
                  property: 'updatedAt',
                  props: {
                    className: 'whitespace-nowrap'
                  },
                  header: {
                    label: intl.formatMessage(messages.updatedAt),
                    props: { className: '!bg-gray-100 !font-bold !text-gray-600 whitespace-nowrap' },
                    sortable: true
                  },
                  cell: {
                    formatters: [
                      (updatedAt: string) => <div className="text-gray-500">{moment(updatedAt).format('ll')}</div>
                    ]
                  }
                },
                {
                  property: 'updatedBy',
                  props: {
                    className: 'whitespace-nowrap'
                  },
                  header: {
                    label: intl.formatMessage(messages.updatedBy),
                    props: { className: '!bg-gray-100 !font-bold !text-gray-600 whitespace-nowrap' },
                    sortable: true
                  },
                  cell: {
                    formatters: [
                      (updatedBy: string) => (
                        <div className="text-gray-500">
                          <User userId={updatedBy} />
                        </div>
                      )
                    ]
                  }
                },
                {
                  property: 'id',
                  props: {
                    className: 'w-0 text-center'
                  },
                  header: {
                    label: intl.formatMessage(messages.actions),
                    props: { className: '!bg-gray-100 !font-bold !text-gray-600 whitespace-nowrap' }
                  },
                  cell: {
                    props: {
                      className: '!p-0'
                    },
                    formatters: [
                      (_: any, { rowData }: { rowData: ClientConfig }) => (
                        <div className="space-x-3">
                          <button
                            type="button"
                            className="cursor-pointer text-gray-500 hover:text-gray-700"
                            onClick={() => handleUpsertClick(rowData)}
                            title={intl.formatMessage(messages.updateClientConfigAction, { key: rowData.key })}
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                          <button
                            type="button"
                            className="cursor-pointer text-gray-500 hover:text-gray-700"
                            onClick={() => handleDeleteClick(rowData)}
                            title={intl.formatMessage(messages.deleteClientConfigAction, { key: rowData.key })}
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      )
                    ]
                  }
                }
              ]}
              pagination={{
                isClientPaging: true,
                count: clientConfigs.length,
                perPage: 10,
                startIndex: 1
              }}
              rows={clientConfigs}
              rowKey="id"
              sortingColumn={{
                updatedAt: {
                  direction: 'desc',
                  position: 0
                },
                key: {
                  direction: 'asc',
                  position: 1
                }
              }}
            />
          </StyledTableWrapper>
        ) : (
          <div className="my-8 text-center">
            <FolderPlusIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900">
              {intl.formatMessage(messages.noClientConfigsTitle)}
            </h3>
            <p className="mt-1 text-sm text-gray-500">{intl.formatMessage(messages.noClientConfigsDescription)}</p>
            <div className="mt-6">
              <button
                type="button"
                className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                onClick={() => handleUpsertClick()}
              >
                <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
                {intl.formatMessage(messages.addClientConfig)}
              </button>
            </div>
          </div>
        )}
      </StyledPage>

      {/* Upsert */}
      <UpsertForm
        show={action?.type === 'upsert'}
        onClose={handleActionCancel}
        values={action?.type === 'upsert' ? action.data : undefined}
      />

      {/* Delete */}
      <DeleteForm
        show={action?.type === 'delete'}
        onClose={handleActionCancel}
        values={action?.type === 'delete' ? action.data : undefined}
      />
    </div>
  )
}

export default OrganizationClientConfigsPage

const upsertSchema = clientConfigSchema
  .pick({ id: true, key: true, config: true, isDefault: true })
  .partial({ id: true })

type UpsertFormProps = {
  show: boolean
  onClose: () => void
  values?: ClientConfig
}

function UpsertForm({ show, onClose, values }: UpsertFormProps) {
  const intl = useIntl()
  const queryClient = useQueryClient()

  const clientConfigMutation = useMutation<
    ClientConfig,
    { data: { message: string; code: string } },
    [string, RequestInit]
  >({
    mutationKey: ['/v1/admin/organizations/:organizationId/client-configs'],
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['/v1/admin/organizations/:organizationId/client-configs']
      })
      onClose()
    }
  })

  const features = useFeatures()
  const widgets = WIDGETS.filter(widget => {
    // Omit by feture flag, unless being used by the current config
    return features[widget.feature] || widget.value === values?.config?.widget
  })

  const spaces = useAppSelector(getSpaces)

  const form = useForm({
    values,
    resolver: zodResolver(upsertSchema),

    // Prevent a flash when hiding the form
    // (we manually reset values when shown)
    resetOptions: {
      keepValues: true
    },

    // Use a function to avoid referential integrity
    defaultValues: async () =>
      ({
        id: '',
        key: '',
        config: {
          schemaVersion: 1,
          widget: widgets[0]?.value,
          position: '',
          label: '',
          icon: '',
          space: spaces.find(space => space.default)?.id,
          supportUrl: '',
          audience: 'active eq true',
          theme: '',
          prompt: '',
          features: '',
          rules: []
        },
        isDefault: false
      }) satisfies z.infer<typeof upsertSchema> as z.infer<typeof upsertSchema>
  })

  // Track the "latest" values
  const valuesRef = useRef<typeof values>()
  valuesRef.current = values

  // Reset form when shown
  useEffect(() => {
    if (show) form.reset(_.merge({}, form.formState.defaultValues, valuesRef.current), { keepValues: false })
  }, [form, show])

  const handleUpsertSubmit: Parameters<typeof form.handleSubmit>[0] = ({ id, ...data }) => {
    if (id) {
      clientConfigMutation.mutate([
        `/v1/admin/organizations/:organizationId/client-configs/${id}`,
        {
          method: 'put',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data)
        }
      ])
    } else {
      clientConfigMutation.mutate([
        `/v1/admin/organizations/:organizationId/client-configs`,
        {
          method: 'post',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data)
        }
      ])
    }
  }

  const rulesField = useFieldArray({
    control: form.control,
    name: 'config.rules'
  })

  const [selectedRule, setSelectedRule] = useState<number>()

  // Scroll to the bottom when adding a new rule
  const scrollRef = useRef<HTMLDivElement>(null)
  const previousRulesLength = useRef(0)
  const rulesLength = form.watch('config.rules')?.length ?? 0
  useLayoutEffect(() => {
    if (rulesLength > previousRulesLength.current && scrollRef.current) {
      const top = scrollRef.current.scrollHeight - scrollRef.current.clientHeight
      scrollRef.current.scrollTo({ top, behavior: 'smooth' })
    }
    previousRulesLength.current = rulesLength
  }, [rulesLength])

  return (
    <Panel show={show} onClose={() => {}} size="lg">
      <form
        onSubmit={form.handleSubmit(handleUpsertSubmit)}
        autoComplete="off"
        className="flex max-h-full flex-1 flex-col"
      >
        <input type="hidden" {...form.register('id')} />
        <input type="hidden" {...form.register('config.schemaVersion')} />

        <div className="flex items-center justify-between bg-gray-700 p-6">
          <Panel.Title as="h3" className="text-lg font-medium leading-6 text-white">
            {intl.formatMessage(form.watch('id') ? messages.updateClientConfig : messages.createClientConfig)}
          </Panel.Title>

          <div className="flex flex-row-reverse gap-3">
            <button
              type="submit"
              className={cn(
                'inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-600'
              )}
              disabled={clientConfigMutation.isLoading}
            >
              {intl.formatMessage(form.watch('id') ? messages.update : messages.create)}
            </button>
            <button
              type="button"
              className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
              onClick={onClose}
            >
              {intl.formatMessage(messages.cancel)}
            </button>
          </div>
        </div>

        <div ref={scrollRef} className="h-0 flex-1 space-y-6 overflow-auto p-6">
          {clientConfigMutation.error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <XCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">{clientConfigMutation.error.data.message}</h3>
                </div>
              </div>
            </div>
          )}

          <Controller
            control={form.control}
            name="key"
            render={({ field, fieldState: { error } }) => (
              <div>
                <label htmlFor={field.name} className="text-sm font-medium leading-6 text-gray-900">
                  {intl.formatMessage(messages.keyLabel)}
                </label>
                <div className="text-sm text-gray-500">{intl.formatMessage(messages.keyDescription)}</div>

                <div className="mt-2">
                  <input
                    type="text"
                    id={field.name}
                    className={cn('block w-full rounded-md border-gray-300 text-sm shadow-sm', {
                      'border-0 text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-2 focus:ring-inset focus:ring-red-500':
                        error
                    })}
                    aria-invalid={error ? true : false}
                    aria-describedby={error && field.name + '-error'}
                    {...field}
                    value={field.value ?? ''} // Ensure component is never "uncontrolled"
                  />
                </div>
                {error && (
                  <p className="mt-2 text-sm text-red-600" id={field.name + '-error'}>
                    {String(error.message)}
                  </p>
                )}
              </div>
            )}
          />

          <Disclosure defaultOpen>
            <DisclosureButton className="group w-full">
              <div className="-mx-6 flex items-center justify-between bg-gray-50 px-6 py-4">
                <h2 className="flex items-center gap-1 text-base font-semibold leading-7 text-gray-900">
                  <ChevronRightIcon className="w-5 transition group-data-[open]:rotate-90" />
                  {intl.formatMessage(messages.targetingSectionLabel)}
                </h2>
                <p className="text-sm leading-6 text-gray-600">
                  {intl.formatMessage(messages.targetingSectionDescription)}
                </p>
              </div>
            </DisclosureButton>
            <DisclosurePanel className="flex-1 space-y-6">
              <Controller
                control={form.control}
                name="isDefault"
                render={({ field, fieldState: { error } }) => (
                  <div>
                    <Field as="div" className="flex items-center justify-between">
                      <span className="flex flex-grow flex-col">
                        <Label as="span" className="text-sm font-medium leading-6 text-gray-900" passive>
                          {intl.formatMessage(messages.defaultLabel)}
                        </Label>
                        <Description as="span" className="text-sm text-gray-500">
                          {intl.formatMessage(messages.defaultDescription)}
                        </Description>
                      </span>
                      <Switch name={field.name} checked={field.value} onChange={checked => field.onChange(checked)}>
                        {({ checked }) => (
                          <span
                            className={cn(
                              checked ? 'bg-blue-600' : 'bg-gray-200',
                              'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2'
                            )}
                          >
                            <span
                              aria-hidden="true"
                              className={cn(
                                checked ? 'translate-x-5' : 'translate-x-0',
                                'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                              )}
                            />
                          </span>
                        )}
                      </Switch>
                    </Field>
                    {error && (
                      <p className="mt-2 text-sm text-red-600" id={field.name + '-error'}>
                        {String(error.message)}
                      </p>
                    )}
                  </div>
                )}
              />

              <Controller
                control={form.control}
                name="config.audience"
                render={({ field, fieldState: { error } }) => (
                  <div>
                    <label htmlFor={field.name} className="text-sm font-medium leading-6 text-gray-900">
                      {intl.formatMessage(messages.audienceLabel)}
                    </label>
                    <div className="text-sm text-gray-500">{intl.formatMessage(messages.audienceDescription)}</div>

                    <div className="mt-2">
                      <Audience id={field.name} value={field.value} onChange={field.onChange} />
                    </div>
                    {error && (
                      <p className="mt-2 text-sm text-red-600" id={field.name + '-error'}>
                        {String(error.message)}
                      </p>
                    )}
                  </div>
                )}
              />
            </DisclosurePanel>
          </Disclosure>

          <Disclosure defaultOpen>
            <DisclosureButton className="group w-full">
              <div className="-mx-6 flex items-center justify-between bg-gray-50 px-6 py-4">
                <h2 className="flex items-center gap-1 text-base font-semibold leading-7 text-gray-900">
                  <ChevronRightIcon className="w-5 transition group-data-[open]:rotate-90" />
                  {intl.formatMessage(messages.configurationSectionLabel)}
                </h2>
                <p className="text-sm leading-6 text-gray-600">
                  {intl.formatMessage(messages.configurationSectionDescription)}
                </p>
              </div>
            </DisclosureButton>
            <DisclosurePanel className="flex-1 space-y-6">
              <Controller
                control={form.control}
                name="config.space"
                render={({ field, fieldState: { error } }) => (
                  <div>
                    <label htmlFor={field.name} className="text-sm font-medium leading-6 text-gray-900">
                      {intl.formatMessage(messages.spaceIdLabel)}
                    </label>
                    <div className="text-sm text-gray-500">{intl.formatMessage(messages.spaceIdDescription)}</div>

                    <div className="mt-2">
                      {form.watch('key') === 'workgrid-console' ? (
                        <div className="text-sm italic">
                          {intl.formatMessage(messages.notApplicable, { key: form.watch('key') })}
                        </div>
                      ) : (
                        <select
                          id={field.name}
                          className={cn('block w-full rounded-md border-gray-300 text-sm shadow-sm', {
                            'border-0 text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-2 focus:ring-inset focus:ring-red-500':
                              error
                          })}
                          aria-invalid={error ? true : false}
                          aria-describedby={error && field.name + '-error'}
                          {...field}
                          value={field.value ?? ''} // Ensure component is never "uncontrolled"
                        >
                          <option value=""></option>
                          {spaces
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map(space => (
                              <option key={space.id} value={space.id}>
                                {space.default ? `${space.name} (Default)` : space.name}
                              </option>
                            ))}
                        </select>
                      )}
                    </div>
                    {error && (
                      <p className="mt-2 text-sm text-red-600" id={field.name + '-error'}>
                        {String(error.message)}
                      </p>
                    )}
                  </div>
                )}
              />

              <Controller
                control={form.control}
                name="config.supportUrl"
                render={({ field, fieldState: { error } }) => (
                  <div>
                    <label htmlFor={field.name} className="text-sm font-medium leading-6 text-gray-900">
                      {intl.formatMessage(messages.supportUrlLabel)}
                    </label>
                    <div className="text-sm text-gray-500">{intl.formatMessage(messages.supportUrlDescription)}</div>

                    <div className="mt-2">
                      <input
                        type="url"
                        id={field.name}
                        className={cn('block w-full rounded-md border-gray-300 text-sm shadow-sm', {
                          'border-0 text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-2 focus:ring-inset focus:ring-red-500':
                            error
                        })}
                        aria-invalid={error ? true : false}
                        aria-describedby={error && field.name + '-error'}
                        {...field}
                        value={field.value ?? ''} // Ensure component is never "uncontrolled"
                      />
                    </div>
                    {error && (
                      <p className="mt-2 text-sm text-red-600" id={field.name + '-error'}>
                        {String(error.message)}
                      </p>
                    )}
                  </div>
                )}
              />

              <Controller
                control={form.control}
                name="config.label"
                render={({ field, fieldState: { error } }) => (
                  <div>
                    <label htmlFor={field.name} className="text-sm font-medium leading-6 text-gray-900">
                      {intl.formatMessage(messages.labelLabel)}
                    </label>
                    <div className="text-sm text-gray-500">{intl.formatMessage(messages.labelDescription)}</div>

                    <div className="mt-2">
                      <input
                        type="text"
                        id={field.name}
                        className={cn('block w-full rounded-md border-gray-300 text-sm shadow-sm', {
                          'border-0 text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-2 focus:ring-inset focus:ring-red-500':
                            error
                        })}
                        aria-invalid={error ? true : false}
                        aria-describedby={error && field.name + '-error'}
                        {...field}
                        value={field.value ?? ''} // Ensure component is never "uncontrolled"
                      />
                    </div>
                    {error && (
                      <p className="mt-2 text-sm text-red-600" id={field.name + '-error'}>
                        {String(error.message)}
                      </p>
                    )}
                  </div>
                )}
              />

              <Controller
                control={form.control}
                name="config.icon"
                render={({ field, fieldState: { error } }) => (
                  <div>
                    <label htmlFor={field.name} className="text-sm font-medium leading-6 text-gray-900">
                      {intl.formatMessage(messages.iconUrlLabel)}
                    </label>
                    <div className="text-sm text-gray-500">{intl.formatMessage(messages.iconUrlDescription)}</div>

                    <div className="mt-2">
                      <input
                        type="url"
                        id={field.name}
                        className={cn('block w-full rounded-md border-gray-300 text-sm shadow-sm', {
                          'border-0 text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-2 focus:ring-inset focus:ring-red-500':
                            error
                        })}
                        aria-invalid={error ? true : false}
                        aria-describedby={error && field.name + '-error'}
                        {...field}
                        value={field.value ?? ''} // Ensure component is never "uncontrolled"
                      />
                    </div>
                    {error && (
                      <p className="mt-2 text-sm text-red-600" id={field.name + '-error'}>
                        {String(error.message)}
                      </p>
                    )}
                  </div>
                )}
              />

              <Controller
                control={form.control}
                name="config.widget"
                render={({ field, fieldState: { error } }) => (
                  <div className={cn({ hidden: widgets.length < 2 })}>
                    <label htmlFor={field.name} className="text-sm font-medium leading-6 text-gray-900">
                      {intl.formatMessage(messages.widgetLabel)}
                    </label>
                    <div className="text-sm text-gray-500">{intl.formatMessage(messages.widgetDescription)}</div>

                    <div className="mt-2">
                      <select
                        id={field.name}
                        className={cn('block w-full rounded-md border-gray-300 text-sm shadow-sm', {
                          'border-0 text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-2 focus:ring-inset focus:ring-red-500':
                            error
                        })}
                        aria-invalid={error ? true : false}
                        aria-describedby={error && field.name + '-error'}
                        {...field}
                        value={field.value ?? ''} // Ensure component is never "uncontrolled"
                      >
                        <option value=""></option>
                        {widgets.map(widget => (
                          <option key={widget.value} value={widget.value}>
                            {widget.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    {error && (
                      <p className="mt-2 text-sm text-red-600" id={field.name + '-error'}>
                        {String(error.message)}
                      </p>
                    )}
                  </div>
                )}
              />

              <Controller
                control={form.control}
                name="config.position"
                render={({ field, fieldState: { error } }) => (
                  <div>
                    <label htmlFor={field.name} className="text-sm font-medium leading-6 text-gray-900">
                      {intl.formatMessage(messages.positionLabel)}
                    </label>
                    <div className="text-sm text-gray-500">{intl.formatMessage(messages.positionDescription)}</div>

                    <div className="mt-2">
                      <input
                        type="text"
                        id={field.name}
                        className={cn('block w-full rounded-md border-gray-300 text-sm shadow-sm', {
                          'border-0 text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-2 focus:ring-inset focus:ring-red-500':
                            error
                        })}
                        aria-invalid={error ? true : false}
                        aria-describedby={error && field.name + '-error'}
                        {...field}
                        value={field.value ?? ''} // Ensure component is never "uncontrolled"
                      />
                    </div>
                    {error && (
                      <p className="mt-2 text-sm text-red-600" id={field.name + '-error'}>
                        {String(error.message)}
                      </p>
                    )}
                  </div>
                )}
              />

              <Controller
                control={form.control}
                name="config.theme"
                render={({ field, fieldState: { error } }) => (
                  <div>
                    <label htmlFor={field.name} className="text-sm font-medium leading-6 text-gray-900">
                      {intl.formatMessage(messages.themeLabel)}
                    </label>
                    <div className="text-sm text-gray-500">{intl.formatMessage(messages.themeDescription)}</div>

                    <div className="mt-2">
                      <input
                        type="text"
                        id={field.name}
                        className={cn('block w-full rounded-md border-gray-300 font-mono text-sm shadow-sm', {
                          'border-0 text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-2 focus:ring-inset focus:ring-red-500':
                            error
                        })}
                        aria-invalid={error ? true : false}
                        aria-describedby={error && field.name + '-error'}
                        {...field}
                        value={field.value ?? ''} // Ensure component is never "uncontrolled"
                      />
                    </div>
                    {error && (
                      <p className="mt-2 text-sm text-red-600" id={field.name + '-error'}>
                        {String(error.message)}
                      </p>
                    )}
                  </div>
                )}
              />

              <Controller
                control={form.control}
                name="config.prompt"
                render={({ field, fieldState: { error } }) => (
                  <div>
                    <label htmlFor={field.name} className="text-sm font-medium leading-6 text-gray-900">
                      {intl.formatMessage(messages.promptLabel)}
                    </label>
                    <div className="text-sm text-gray-500">{intl.formatMessage(messages.promptDescription)}</div>

                    <div className="mt-2">
                      <input
                        type="text"
                        id={field.name}
                        className={cn('block w-full rounded-md border-gray-300 text-sm shadow-sm', {
                          'border-0 text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-2 focus:ring-inset focus:ring-red-500':
                            error
                        })}
                        aria-invalid={error ? true : false}
                        aria-describedby={error && field.name + '-error'}
                        {...field}
                        value={field.value ?? ''} // Ensure component is never "uncontrolled"
                      />
                    </div>
                    {error && (
                      <p className="mt-2 text-sm text-red-600" id={field.name + '-error'}>
                        {String(error.message)}
                      </p>
                    )}
                  </div>
                )}
              />
            </DisclosurePanel>
          </Disclosure>

          <Disclosure>
            <DisclosureButton className="group w-full">
              <div className="-mx-6 flex items-center justify-between bg-gray-50 px-6 py-4">
                <h2 className="flex items-center gap-1 text-base font-semibold leading-7 text-gray-900">
                  <ChevronRightIcon className="w-5 transition group-data-[open]:rotate-90" />
                  {intl.formatMessage(messages.advancedSectionLabel)}
                </h2>
                <p className="text-sm leading-6 text-gray-600">
                  {intl.formatMessage(messages.advancedSectionDescription)}
                </p>
              </div>
            </DisclosureButton>
            <DisclosurePanel className="flex-1 space-y-6">
              <Controller
                control={form.control}
                name="config.features"
                render={({ field, fieldState: { error } }) => (
                  <div>
                    <label htmlFor={field.name} className="text-sm font-medium leading-6 text-gray-900">
                      {intl.formatMessage(messages.featuresLabel)}
                    </label>
                    <div className="text-sm text-gray-500">{intl.formatMessage(messages.featuresDescription)}</div>

                    <div className="mt-2">
                      <textarea
                        id={field.name}
                        rows={4}
                        className={cn('block w-full rounded-md border-gray-300 text-sm shadow-sm', {
                          'border-0 text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-2 focus:ring-inset focus:ring-red-500':
                            error
                        })}
                        aria-invalid={error ? true : false}
                        aria-describedby={error && field.name + '-error'}
                        {...field}
                        value={stringifyObject(field.value) ?? ''} // Ensure component is never "uncontrolled"
                      />
                    </div>
                    {error && (
                      <p className="mt-2 text-sm text-red-600" id={field.name + '-error'}>
                        {String(error.message)}
                      </p>
                    )}
                  </div>
                )}
              />

              <div>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium leading-6 text-gray-900">
                      {intl.formatMessage(messages.rulesLabel)}
                    </div>
                    <div className="text-sm text-gray-500">{intl.formatMessage(messages.rulesDescription)}</div>
                  </div>

                  <button
                    type="button"
                    className="mt-2 inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                    onClick={() => {
                      setSelectedRule(rulesField.fields.length)
                      rulesField.append({ name: 'Rule Name' })
                    }}
                  >
                    Add Rule
                  </button>
                </div>

                <div className={cn('mt-2 flex flex-col gap-2', { hidden: rulesField.fields.length === 0 })}>
                  <Sortable
                    items={rulesField.fields}
                    onDragEnd={({ active, over }) => {
                      if (over?.id && active.id !== over.id) {
                        rulesField.move(
                          rulesField.fields.findIndex(field => field.id === active.id),
                          rulesField.fields.findIndex(field => field.id === over.id)
                        )
                      }
                    }}
                  >
                    {rulesField.fields.map((field, index) => {
                      const name = form.getValues(`config.rules.${index}.name`)
                      return (
                        <SortableItem key={field.id} id={field.id}>
                          {sortable => (
                            <div
                              ref={sortable.setNodeRef}
                              style={sortable.style}
                              className={cn('bg-gray-100 p-3', { 'z-[1] shadow': sortable.isDragging })}
                            >
                              <div className="flex items-center gap-2">
                                <div
                                  ref={sortable.setActivatorNodeRef}
                                  {...sortable.attributes}
                                  {...sortable.listeners}
                                >
                                  <Bars3Icon className="h-5 w-5 text-gray-700" />
                                </div>

                                <div className="w-full">{name}</div>

                                <div className="flex gap-0.5">
                                  <button
                                    type="button"
                                    className="rounded-md p-2 text-gray-700 hover:bg-white"
                                    onClick={() => setSelectedRule(index)}
                                  >
                                    <PencilIcon className="h-4 w-4" title={`Edit ${name}`} />
                                  </button>
                                  <button
                                    type="button"
                                    className="rounded-md p-2 text-gray-700 hover:bg-white"
                                    onClick={() => rulesField.remove(index)}
                                  >
                                    <TrashIcon className="h-4 w-4" title={`Remove ${name}`} />
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </SortableItem>
                      )
                    })}
                  </Sortable>
                </div>

                <Panel show={selectedRule != null} onClose={() => {}} size="lg">
                  <div className="flex max-h-full flex-1 flex-col">
                    <div className="flex items-center justify-between bg-gray-700 p-6">
                      <Panel.Title as="h3" className="text-lg font-medium leading-6 text-white">
                        {intl.formatMessage(messages.editRule)}
                      </Panel.Title>

                      <div className="flex flex-row-reverse gap-3">
                        <button
                          type="button"
                          className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                          onClick={async () => {
                            const valid = await form.trigger([`config.rules.${selectedRule!}`], { shouldFocus: true })
                            if (valid) setSelectedRule(undefined)
                          }}
                        >
                          Done
                        </button>
                      </div>
                    </div>

                    <div className="h-0 flex-1 space-y-6 overflow-auto p-6">
                      <Controller
                        control={form.control}
                        name={`config.rules.${selectedRule!}.name`}
                        render={({ field, fieldState: { error } }) => (
                          <div>
                            <label htmlFor={field.name} className="text-sm font-medium leading-6 text-gray-900">
                              {intl.formatMessage(messages.nameLabel)}
                            </label>
                            <div className="text-sm text-gray-500">{intl.formatMessage(messages.nameDescription)}</div>

                            <div className="mt-2">
                              <input
                                type="text"
                                id={field.name}
                                className={cn('block w-full rounded-md border-gray-300 text-sm shadow-sm', {
                                  'border-0 text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-2 focus:ring-inset focus:ring-red-500':
                                    error
                                })}
                                aria-invalid={error ? true : false}
                                aria-describedby={error && field.name + '-error'}
                                {...field}
                                value={field.value ?? ''} // Ensure component is never "uncontrolled"
                              />
                            </div>
                            {error && (
                              <p className="mt-2 text-sm text-red-600" id={field.name + '-error'}>
                                {String(error.message)}
                              </p>
                            )}
                          </div>
                        )}
                      />

                      <Disclosure defaultOpen>
                        <DisclosureButton className="group w-full">
                          <div className="-mx-6 flex items-center justify-between bg-gray-50 px-6 py-4">
                            <h2 className="flex items-center gap-1 text-base font-semibold leading-7 text-gray-900">
                              <ChevronRightIcon className="w-5 transition group-data-[open]:rotate-90" />
                              {intl.formatMessage(messages.targetingSectionLabel)}
                            </h2>
                            <p className="text-sm leading-6 text-gray-600">
                              {intl.formatMessage(messages.targetingSectionDescription)}
                            </p>
                          </div>
                        </DisclosureButton>
                        <DisclosurePanel className="flex-1 space-y-6">
                          <Controller
                            control={form.control}
                            name={`config.rules.${selectedRule!}.audience`}
                            render={({ field, fieldState: { error } }) => (
                              <div>
                                <label htmlFor={field.name} className="text-sm font-medium leading-6 text-gray-900">
                                  {intl.formatMessage(messages.audienceLabel)}
                                </label>
                                <div className="text-sm text-gray-500">
                                  {intl.formatMessage(messages.audienceDescription)}
                                </div>

                                <div className="mt-2">
                                  <Audience id={field.name} value={field.value} onChange={field.onChange} />
                                </div>
                                {error && (
                                  <p className="mt-2 text-sm text-red-600" id={field.name + '-error'}>
                                    {String(error.message)}
                                  </p>
                                )}
                              </div>
                            )}
                          />
                        </DisclosurePanel>
                      </Disclosure>

                      <Disclosure defaultOpen>
                        <DisclosureButton className="group w-full">
                          <div className="-mx-6 flex items-center justify-between bg-gray-50 px-6 py-4">
                            <h2 className="flex items-center gap-1 text-base font-semibold leading-7 text-gray-900">
                              <ChevronRightIcon className="w-5 transition group-data-[open]:rotate-90" />
                              {intl.formatMessage(messages.configurationSectionLabel)}
                            </h2>
                            <p className="text-sm leading-6 text-gray-600">
                              {intl.formatMessage(messages.configurationSectionDescription)}
                            </p>
                          </div>
                        </DisclosureButton>
                        <DisclosurePanel className="flex-1 space-y-6">
                          <Controller
                            control={form.control}
                            name={`config.rules.${selectedRule!}.space`}
                            render={({ field, fieldState: { error } }) => (
                              <div>
                                <label htmlFor={field.name} className="text-sm font-medium leading-6 text-gray-900">
                                  {intl.formatMessage(messages.spaceIdLabel)}
                                </label>
                                <div className="text-sm text-gray-500">
                                  {intl.formatMessage(messages.spaceIdDescription)}
                                </div>

                                <div className="mt-2">
                                  {form.watch('key') === 'workgrid-console' ? (
                                    <div className="text-sm italic">
                                      {intl.formatMessage(messages.notApplicable, { key: form.watch('key') })}
                                    </div>
                                  ) : (
                                    <select
                                      id={field.name}
                                      className={cn('block w-full rounded-md border-gray-300 text-sm shadow-sm', {
                                        'border-0 text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-2 focus:ring-inset focus:ring-red-500':
                                          error
                                      })}
                                      aria-invalid={error ? true : false}
                                      aria-describedby={error && field.name + '-error'}
                                      {...field}
                                      value={field.value ?? ''} // Ensure component is never "uncontrolled"
                                    >
                                      <option value=""></option>
                                      {spaces
                                        .sort((a, b) => a.name.localeCompare(b.name))
                                        .map(space => (
                                          <option key={space.id} value={space.id}>
                                            {space.default ? `${space.name} (Default)` : space.name}
                                          </option>
                                        ))}
                                    </select>
                                  )}
                                </div>
                                {error && (
                                  <p className="mt-2 text-sm text-red-600" id={field.name + '-error'}>
                                    {String(error.message)}
                                  </p>
                                )}
                              </div>
                            )}
                          />

                          <Controller
                            control={form.control}
                            name={`config.rules.${selectedRule!}.supportUrl`}
                            render={({ field, fieldState: { error } }) => (
                              <div>
                                <label htmlFor={field.name} className="text-sm font-medium leading-6 text-gray-900">
                                  {intl.formatMessage(messages.supportUrlLabel)}
                                </label>
                                <div className="text-sm text-gray-500">
                                  {intl.formatMessage(messages.supportUrlDescription)}
                                </div>

                                <div className="mt-2">
                                  <input
                                    type="url"
                                    id={field.name}
                                    className={cn('block w-full rounded-md border-gray-300 text-sm shadow-sm', {
                                      'border-0 text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-2 focus:ring-inset focus:ring-red-500':
                                        error
                                    })}
                                    aria-invalid={error ? true : false}
                                    aria-describedby={error && field.name + '-error'}
                                    {...field}
                                    value={field.value ?? ''} // Ensure component is never "uncontrolled"
                                  />
                                </div>
                                {error && (
                                  <p className="mt-2 text-sm text-red-600" id={field.name + '-error'}>
                                    {String(error.message)}
                                  </p>
                                )}
                              </div>
                            )}
                          />

                          <Controller
                            control={form.control}
                            name={`config.rules.${selectedRule!}.label`}
                            render={({ field, fieldState: { error } }) => (
                              <div>
                                <label htmlFor={field.name} className="text-sm font-medium leading-6 text-gray-900">
                                  {intl.formatMessage(messages.labelLabel)}
                                </label>
                                <div className="text-sm text-gray-500">
                                  {intl.formatMessage(messages.labelDescription)}
                                </div>

                                <div className="mt-2">
                                  <input
                                    type="text"
                                    id={field.name}
                                    className={cn('block w-full rounded-md border-gray-300 text-sm shadow-sm', {
                                      'border-0 text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-2 focus:ring-inset focus:ring-red-500':
                                        error
                                    })}
                                    aria-invalid={error ? true : false}
                                    aria-describedby={error && field.name + '-error'}
                                    {...field}
                                    value={field.value ?? ''} // Ensure component is never "uncontrolled"
                                  />
                                </div>
                                {error && (
                                  <p className="mt-2 text-sm text-red-600" id={field.name + '-error'}>
                                    {String(error.message)}
                                  </p>
                                )}
                              </div>
                            )}
                          />

                          <Controller
                            control={form.control}
                            name={`config.rules.${selectedRule!}.icon`}
                            render={({ field, fieldState: { error } }) => (
                              <div>
                                <label htmlFor={field.name} className="text-sm font-medium leading-6 text-gray-900">
                                  {intl.formatMessage(messages.iconUrlLabel)}
                                </label>
                                <div className="text-sm text-gray-500">
                                  {intl.formatMessage(messages.iconUrlDescription)}
                                </div>

                                <div className="mt-2">
                                  <input
                                    type="url"
                                    id={field.name}
                                    className={cn('block w-full rounded-md border-gray-300 text-sm shadow-sm', {
                                      'border-0 text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-2 focus:ring-inset focus:ring-red-500':
                                        error
                                    })}
                                    aria-invalid={error ? true : false}
                                    aria-describedby={error && field.name + '-error'}
                                    {...field}
                                    value={field.value ?? ''} // Ensure component is never "uncontrolled"
                                  />
                                </div>
                                {error && (
                                  <p className="mt-2 text-sm text-red-600" id={field.name + '-error'}>
                                    {String(error.message)}
                                  </p>
                                )}
                              </div>
                            )}
                          />

                          <Controller
                            control={form.control}
                            name={`config.rules.${selectedRule!}.widget`}
                            render={({ field, fieldState: { error } }) => (
                              <div className={cn({ hidden: widgets.length < 2 })}>
                                <label htmlFor={field.name} className="text-sm font-medium leading-6 text-gray-900">
                                  {intl.formatMessage(messages.widgetLabel)}
                                </label>
                                <div className="text-sm text-gray-500">
                                  {intl.formatMessage(messages.widgetDescription)}
                                </div>

                                <div className="mt-2">
                                  <select
                                    id={field.name}
                                    className={cn('block w-full rounded-md border-gray-300 text-sm shadow-sm', {
                                      'border-0 text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-2 focus:ring-inset focus:ring-red-500':
                                        error
                                    })}
                                    aria-invalid={error ? true : false}
                                    aria-describedby={error && field.name + '-error'}
                                    {...field}
                                    value={field.value ?? ''} // Ensure component is never "uncontrolled"
                                  >
                                    <option value=""></option>
                                    {widgets.map(widget => (
                                      <option key={widget.value} value={widget.value}>
                                        {widget.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                {error && (
                                  <p className="mt-2 text-sm text-red-600" id={field.name + '-error'}>
                                    {String(error.message)}
                                  </p>
                                )}
                              </div>
                            )}
                          />

                          <Controller
                            control={form.control}
                            name={`config.rules.${selectedRule!}.position`}
                            render={({ field, fieldState: { error } }) => (
                              <div>
                                <label htmlFor={field.name} className="text-sm font-medium leading-6 text-gray-900">
                                  {intl.formatMessage(messages.positionLabel)}
                                </label>
                                <div className="text-sm text-gray-500">
                                  {intl.formatMessage(messages.positionDescription)}
                                </div>

                                <div className="mt-2">
                                  <input
                                    type="text"
                                    id={field.name}
                                    className={cn('block w-full rounded-md border-gray-300 text-sm shadow-sm', {
                                      'border-0 text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-2 focus:ring-inset focus:ring-red-500':
                                        error
                                    })}
                                    aria-invalid={error ? true : false}
                                    aria-describedby={error && field.name + '-error'}
                                    {...field}
                                    value={field.value ?? ''} // Ensure component is never "uncontrolled"
                                  />
                                </div>
                                {error && (
                                  <p className="mt-2 text-sm text-red-600" id={field.name + '-error'}>
                                    {String(error.message)}
                                  </p>
                                )}
                              </div>
                            )}
                          />

                          <Controller
                            control={form.control}
                            name={`config.rules.${selectedRule!}.theme`}
                            render={({ field, fieldState: { error } }) => (
                              <div>
                                <label htmlFor={field.name} className="text-sm font-medium leading-6 text-gray-900">
                                  {intl.formatMessage(messages.themeLabel)}
                                </label>
                                <div className="text-sm text-gray-500">
                                  {intl.formatMessage(messages.themeDescription)}
                                </div>

                                <div className="mt-2">
                                  <input
                                    type="text"
                                    id={field.name}
                                    className={cn(
                                      'block w-full rounded-md border-gray-300 font-mono text-sm shadow-sm',
                                      {
                                        'border-0 text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-2 focus:ring-inset focus:ring-red-500':
                                          error
                                      }
                                    )}
                                    aria-invalid={error ? true : false}
                                    aria-describedby={error && field.name + '-error'}
                                    {...field}
                                    value={field.value ?? ''} // Ensure component is never "uncontrolled"
                                  />
                                </div>
                                {error && (
                                  <p className="mt-2 text-sm text-red-600" id={field.name + '-error'}>
                                    {String(error.message)}
                                  </p>
                                )}
                              </div>
                            )}
                          />

                          <Controller
                            control={form.control}
                            name={`config.rules.${selectedRule!}.prompt`}
                            render={({ field, fieldState: { error } }) => (
                              <div>
                                <label htmlFor={field.name} className="text-sm font-medium leading-6 text-gray-900">
                                  {intl.formatMessage(messages.promptLabel)}
                                </label>
                                <div className="text-sm text-gray-500">
                                  {intl.formatMessage(messages.promptDescription)}
                                </div>

                                <div className="mt-2">
                                  <input
                                    type="text"
                                    id={field.name}
                                    className={cn('block w-full rounded-md border-gray-300 text-sm shadow-sm', {
                                      'border-0 text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-2 focus:ring-inset focus:ring-red-500':
                                        error
                                    })}
                                    aria-invalid={error ? true : false}
                                    aria-describedby={error && field.name + '-error'}
                                    {...field}
                                    value={field.value ?? ''} // Ensure component is never "uncontrolled"
                                  />
                                </div>
                                {error && (
                                  <p className="mt-2 text-sm text-red-600" id={field.name + '-error'}>
                                    {String(error.message)}
                                  </p>
                                )}
                              </div>
                            )}
                          />
                        </DisclosurePanel>
                      </Disclosure>

                      <Disclosure>
                        <DisclosureButton className="group w-full">
                          <div className="-mx-6 flex items-center justify-between bg-gray-50 px-6 py-4">
                            <h2 className="flex items-center gap-1 text-base font-semibold leading-7 text-gray-900">
                              <ChevronRightIcon className="w-5 transition group-data-[open]:rotate-90" />
                              {intl.formatMessage(messages.advancedSectionLabel)}
                            </h2>
                            <p className="text-sm leading-6 text-gray-600">
                              {intl.formatMessage(messages.advancedSectionDescription)}
                            </p>
                          </div>
                        </DisclosureButton>
                        <DisclosurePanel className="flex-1 space-y-6">
                          <Controller
                            control={form.control}
                            name={`config.rules.${selectedRule!}.features`}
                            render={({ field, fieldState: { error } }) => (
                              <div>
                                <label htmlFor={field.name} className="text-sm font-medium leading-6 text-gray-900">
                                  {intl.formatMessage(messages.featuresLabel)}
                                </label>
                                <div className="text-sm text-gray-500">
                                  {intl.formatMessage(messages.featuresDescription)}
                                </div>

                                <div className="mt-2">
                                  <textarea
                                    id={field.name}
                                    rows={4}
                                    className={cn('block w-full rounded-md border-gray-300 text-sm shadow-sm', {
                                      'border-0 text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-2 focus:ring-inset focus:ring-red-500':
                                        error
                                    })}
                                    aria-invalid={error ? true : false}
                                    aria-describedby={error && field.name + '-error'}
                                    {...field}
                                    value={stringifyObject(field.value) ?? ''} // Ensure component is never "uncontrolled"
                                  />
                                </div>
                                {error && (
                                  <p className="mt-2 text-sm text-red-600" id={field.name + '-error'}>
                                    {String(error.message)}
                                  </p>
                                )}
                              </div>
                            )}
                          />
                        </DisclosurePanel>
                      </Disclosure>
                    </div>
                  </div>
                </Panel>
              </div>
            </DisclosurePanel>
          </Disclosure>
        </div>
      </form>
    </Panel>
  )
}

const deleteSchema = clientConfigSchema.pick({ id: true, key: true })

type DeleteFormProps = {
  show: boolean
  onClose: () => void
  values?: ClientConfig
}

function DeleteForm({ show, onClose, values }: DeleteFormProps) {
  const intl = useIntl()
  const queryClient = useQueryClient()

  const clientConfigMutation = useMutation<
    ClientConfig,
    { data: { message: string; code: string } },
    [string, RequestInit]
  >({
    mutationKey: ['/v1/admin/organizations/:organizationId/client-configs'],
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['/v1/admin/organizations/:organizationId/client-configs']
      })
      onClose()
    }
  })

  const form = useForm({
    values,
    resolver: zodResolver(deleteSchema),

    // Prevent a flash when hiding the form
    // (we manually reset values when shown)
    resetOptions: {
      keepValues: true
    }
  })

  // Track the "latest" values
  const valuesRef = useRef<typeof values>()
  valuesRef.current = values

  // Reset form when shown
  useEffect(() => {
    if (show) form.reset(_.merge({}, form.formState.defaultValues, valuesRef.current), { keepValues: false })
  }, [form, show])

  const handleDeleteSubmit: Parameters<typeof form.handleSubmit>[0] = ({ id }) => {
    clientConfigMutation.mutate([
      `/v1/admin/organizations/:organizationId/client-configs/${id}`,
      {
        method: 'delete'
      }
    ])
  }

  return (
    <Modal show={show} onClose={() => {}}>
      <form
        onSubmit={form.handleSubmit(handleDeleteSubmit)}
        autoComplete="off"
        className="flex max-h-full flex-1 flex-col"
      >
        <input type="hidden" {...form.register('id')} />

        <div className="p-6 pb-0">
          <Modal.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
            {intl.formatMessage(messages.deleteClientConfig)}
          </Modal.Title>
        </div>

        <div className="h-0 flex-1 space-y-6 overflow-auto p-6">
          {clientConfigMutation.error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <XCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">{clientConfigMutation.error.data.message}</h3>
                </div>
              </div>
            </div>
          )}

          <div>
            <p className="block text-sm font-medium leading-6 text-gray-900">
              {intl.formatMessage(messages.deleteConfirmation, { key: <strong>{form.watch('key')}</strong> })}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end bg-gray-50 py-3 px-6">
          <div className="flex flex-row-reverse gap-3">
            <button
              type="submit"
              className={cn(
                'inline-flex justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700'
              )}
              disabled={clientConfigMutation.isLoading}
            >
              {intl.formatMessage(messages.delete)}
            </button>
            <button
              type="button"
              className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
              onClick={onClose}
            >
              {intl.formatMessage(messages.cancel)}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  )
}
